import { useEffect, useMemo, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi, type FloorBox, type FloorBoxLabelData } from "../../api/shopMode";
import { isPrinterSupported, printRaster } from "../../platform/printer";
import { resolvePrinterFor, type ResolvedPrinter } from "../../platform/printerRegistry";
import { tuningToJobOptions } from "../../platform/printSettings";
import { toCatPrinterJob } from "../../receipt/catPrinter";
import { renderLabel, type RenderedLabel } from "../../receipt/labelRender";
import { NotePreview } from "./NotePreview";
import { PairPrinterPrompt } from "./PairPrinterPrompt";
import { useT } from "../../i18n/LanguageProvider";
import { t as translate } from "../../i18n/t";

const DEFAULT_WIDTH_DOTS = 384;

/**
 * G-84 — "Print Barcode": the label at true size, and the button that burns it.
 *
 * The preview IS the print (the receipt's and the note's rule): one
 * `renderLabel` call feeds both. Printing goes through the `notes` role,
 * falling back to `receipts` — the same 58 mm roll, so no third printer to
 * pair. Outside the shell there is no Bluetooth, so the page says where to
 * print instead of showing a dead button.
 */
export function ShopFloorBoxLabelPrint({ box }: { box: FloorBox }) {
  const { t } = useT();
  const [label, setLabel] = useState<FloorBoxLabelData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // G-97: a setter, because pairing now happens on this screen — without one
  // the button would succeed and the panel would still say nothing is paired.
  const [printer, setPrinter] = useState<ResolvedPrinter | null>(() =>
    resolvePrinterFor("notes"),
  );
  const supported = isPrinterSupported();

  useEffect(() => {
    let alive = true;
    setLabel(null);
    setError(null);
    shopModeApi
      .floorBoxLabel(box.id)
      .then((data) => {
        if (alive) setLabel(data);
      })
      .catch((err: unknown) => {
        if (alive) setError(errorText(err, "box.labelCouldNotLoad"));
      });
    return () => {
      alive = false;
    };
  }, [box.id]);

  const rendered: RenderedLabel | null = useMemo(() => {
    if (!label) return null;
    try {
      return renderLabel(label, printer?.printer.widthDots ?? DEFAULT_WIDTH_DOTS);
    } catch (err: unknown) {
      setError(errorText(err, "box.labelCouldNotDraw"));
      return null;
    }
  }, [label, printer]);

  const handlePrint = async () => {
    if (!rendered || !printer) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await printRaster(
        printer.printer,
        toCatPrinterJob(rendered.bitmap, {
          ...tuningToJobOptions(printer.printer.tuning),
          rowBytes: Math.ceil(printer.printer.widthDots / 8),
        }),
      );
      setDone(translate("box.labelPrinted"));
      void shopModeApi.floorBoxLabelPrinted(box.id);
    } catch (err: unknown) {
      setError(errorText(err, "box.labelCouldNotPrint"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sf-box-label">
      {error ? <div className="alert error">{error}</div> : null}
      {rendered ? (
        <>
          <NotePreview bitmap={rendered.bitmap} />
          {rendered.layout.barcode.tooNarrow ? (
            <p className="alert error">
              {t("print.paperTooNarrow")}
            </p>
          ) : null}
          <p className="muted sf-box-label-len">{rendered.lengthMm} mm of paper</p>
        </>
      ) : !error ? (
        <p className="muted">{t("box.labelPreparing")}</p>
      ) : null}
      {!supported ? (
        <p className="muted sf-box-label-note">
          {t("print.labelFromTabletOnly")}
        </p>
      ) : !printer ? (
        <PairPrinterPrompt onPaired={setPrinter} />
      ) : (
        <button
          type="button"
          className="button shop-floor-action-btn"
          disabled={busy || !rendered}
          onClick={() => void handlePrint()}
        >
          {busy ? t("printer.printing") : t("box.printBarcode")}
        </button>
      )}
      {done ? <div className="alert success">{done}</div> : null}
    </div>
  );
}
